import * as React from 'react';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import {safelyGetFilterByKey} from 'nutshell-core/filter';
import type {FilterObject} from 'nutshell-core/types';

import {ConfirmationDialog} from 'shells/confirmation-dialog/confirmation-dialog';
import {ListPageControlBar} from 'shells/control-bar/list-page-control-bar';
import {CueCard} from 'shells/cue-card/cue-card';
import {Illustration} from 'shells/illustrations/illustration';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';

import {appHistory} from '../../../history';

import {useGetInstanceInfo} from '../../core/use-get-instance-info';
import {PageContainer, PageTitleBar} from '../../layout';

import {useGetManagedMeetingsTemplates} from '../graphql/hooks';

import {CreateManagedMeetingButton} from './create-managed-meeting-button';
// @ts-expect-error
import {useDeleteManagedMeetingTemplate, useUndeleteManagedMeetingTemplate} from './graphql/hooks';
import {ManagedMeetingsTable} from './managed-meetings-table';

type Props = ContextRouter & {
    activeFilters: FilterObject[];
    onUpdateFilters: (arg1: FilterObject) => void;
};

export function ManagedMeetingsListPage(props: Props) {
    const [isConfirmingDeleteId, setIsConfirmingDeleteId] = React.useState<
        string | null | undefined
    >(null);
    const [isCardMinimized, setIsCardMinimized] = React.useState(false);
    const isShowingArchived = safelyGetFilterByKey(props.activeFilters, 'archived') === 'true';

    const {sessionInstance, isLoading: isSessionInstanceLoading} = useGetInstanceInfo();
    const hasSchedulerFullAccess = Boolean(
        sessionInstance && sessionInstance.schedulerAccessType === 'FULL_ACCESS'
    );

    const {managedMeetings, isLoading, error} = useGetManagedMeetingsTemplates(isShowingArchived);

    const deleteMeeting = useDeleteManagedMeetingTemplate(isShowingArchived);
    const handleDelete = (meetingId: string, shouldShowConfirmation: boolean) => {
        if (shouldShowConfirmation) {
            setIsConfirmingDeleteId(meetingId);
        } else {
            return deleteMeeting({id: meetingId});
        }
    };

    const undeleteMeeting = useUndeleteManagedMeetingTemplate(isShowingArchived);
    const handleUndelete = (meetingId: string) => {
        return undeleteMeeting({meetingId});
    };

    return (
        <PageContainer
            title='Scheduler'
            pageTitleBar={
                <PageTitleBar
                    title='Managed meetings'
                    subheaderPrimaryText='Set your organization up for success with consistent standardized meetings'
                    buttons={hasSchedulerFullAccess && <CreateManagedMeetingButton />}
                />
            }
            controlBar={
                hasSchedulerFullAccess && (
                    <ListPageControlBar
                        archiveTab={{
                            isActive: isShowingArchived,
                            onChange: () => {
                                props.onUpdateFilters({
                                    archived: isShowingArchived ? null : true,
                                });
                            },
                        }}
                    />
                )
            }
        >
            <StackLegacy spacing={16}>
                {!isCardMinimized && (
                    <CueCard
                        title='Give your team members a jump start with managed meetings'
                        description='Once a managed meeting is assigned to your users, they can focus on handling their bookings. If any adjustments are needed, you can override their changes at any time.'
                        thumbnailIllustration={<Illustration src='calendar-managed' />}
                        thumbnailBackgroundColor='plumLt'
                        withBookmarkIcon={true}
                        history={appHistory}
                        cta={{
                            text: 'Learn more',
                            actionType: 'external-link',
                            url: 'https://support.nutshell.com/en/articles/8428989-scheduler-plan-schedule-and-manage-meetings',
                        }}
                        onMinimize={() => setIsCardMinimized(true)}
                    />
                )}
                <ManagedMeetingsTable
                    meetings={managedMeetings}
                    isLoading={isLoading || isSessionInstanceLoading}
                    isFirstLoad={false}
                    error={error}
                    isFiltering={isShowingArchived}
                    onDeleteMeeting={handleDelete}
                    onUndeleteMeeting={handleUndelete}
                    hasSchedulerFullAccess={hasSchedulerFullAccess}
                />
            </StackLegacy>
            {isConfirmingDeleteId ? (
                <ConfirmationDialog
                    title='Archive this meeting?'
                    message='All assigned meetings will be archived as well.'
                    onConfirm={() => {
                        deleteMeeting({id: isConfirmingDeleteId});
                        setIsConfirmingDeleteId(null);
                    }}
                    onCancel={() => {
                        setIsConfirmingDeleteId(null);
                    }}
                    confirmText='Archive'
                    cancelText='Cancel'
                />
            ) : undefined}
        </PageContainer>
    );
}
